import React, { useMemo } from "react";
import { SxProps } from "@mui/system";
import { Box, Card, Chip, Typography } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "components/v3/sharedComponents/Translate";
import GLTooltip from "components/v3/sharedComponents/GLTooltip";

import CheckmarkCircleIcon from "../../../../assets/icons/CheckmarkCircleIcon.svg";
import CirclePlusIcon from "../../../../assets/icons/CirclePlusIcon.svg";
import EyeIcon from "../../../../assets/icons/EyeIcon.svg";
import LockIcon from "../../../../assets/icons/LockIcon.svg";
import MoreAltIcon from "../../../../assets/icons/MoreAltIcon.svg";

import CarrierScreeningImage from "../../../../assets/images/CarrierScreening.svg";
import GeneticHealthScreenImage from "../../../../assets/images/GeneticHealthScreen.svg";
import PharmacogenomicsImage from "../../../../assets/images/Pharmacogenomics.svg";

// Help center article URLs by test type
const testDescriptionLinksByType: Record<string, string> = {
  CARRIER:
    "https://www.member.genomiclife.com/content/help-center/articles/carrier-screening-informs-family-planning-and-reproductive-health/",
  GHS:
    "https://www.member.genomiclife.com/content/help-center/articles/the-genetic-health-screen-helps-you-better-understand-your-health/",
  PGX:
    "https://www.member.genomiclife.com/content/help-center/articles/pgx-test-medications-personalized-for-you/"
};

export type Kit = {
  description: string;
  name: string;
  test_name: string;
  test_uuid: string;
  glTestType: string;
};

type TestItem = {
  description: {
    name: string;
    uuid: string;
  };
  glTestUUID?: string;
  glTestType?: string;
};

type SuggestedScreeningCardProps = {
  displayOnly?: boolean;
  existingTestKitOrders?: any[];
  hasFilesReadyToDownload?: boolean;
  downloadFiles?: (productIdentifier: string) => void;
  href?: string;
  kit: Kit;
  onSelect?: (id: string) => void;
  selected?: boolean;
  sx?: SxProps;
  suggested?: boolean;
  userHasOrder: boolean;
};

export const testNameImageMap: Record<string, string> = {
  "Carrier Screening": CarrierScreeningImage,
  "Genetic Health Screen": GeneticHealthScreenImage,
  "Pharmacogenomics (PGx)": PharmacogenomicsImage
};

const getCardImage = (kit: Kit) => {
  const { test_name: testName } = kit;

  return testNameImageMap[testName];
};

const getKitDetails = ({
  existingTestKitOrders = [],
  kit
}: {
  existingTestKitOrders: TestItem[];
  kit: Kit;
}) => {
  return existingTestKitOrders.find((testItem) => {
    return testItem.glTestType === kit?.glTestType;
  });
};

const SuggestedScreeningCard: React.FC<SuggestedScreeningCardProps> = ({
  displayOnly = true,
  downloadFiles,
  existingTestKitOrders = [],
  hasFilesReadyToDownload,
  href,
  kit,
  onSelect,
  selected,
  suggested,
  sx,
  userHasOrder
}) => {
  const productIdentifier = kit.test_uuid ?? "";
  const testName = kit.test_name ?? "";
  const testDescription = kit.description ?? "";
  const kitDetails = getKitDetails({ existingTestKitOrders, kit });
  const hasTestDescription = testDescription?.length > 0;
  const cardImage = getCardImage(kit);

  // Get link from test type (works for any test type, not just specific UUIDs)
  const aboutTestLink = testDescriptionLinksByType[kit.glTestType] || "";
  const isSelectable = !!(suggested && !kitDetails && !hasFilesReadyToDownload);
  const isClickable = !!(
    !displayOnly &&
    (isSelectable || hasFilesReadyToDownload)
  );
  const config = useMemo(() => {
    if (hasFilesReadyToDownload && userHasOrder) {
      return {
        action: () => downloadFiles(productIdentifier),
        actionText: "View results",
        cardColor: COLORS.SECONDARY_LIGHTEST,
        cardSx: {
          cursor: "default",
          ...(isClickable && {
            cursor: "pointer",
            transition: "outline 0.1s linear",
            "&:hover": {
              outline: `2px solid ${COLORS.SECONDARY_LIGHT}`
            }
          })
        },
        icon: EyeIcon,
        label: "Results Available",
        labelColor: COLORS.SECONDARY_LIGHTER,
        showAboutTest: false,
        tooltipText: `You have results available! Please take the time to review your results.`
      };
    }

    if (kitDetails && !hasFilesReadyToDownload) {
      return {
        actionLink:
          "https://www.member.genomiclife.com/content/help-center/articles/contact-us/",
        actionText: "Have a question?",
        cardColor: COLORS.ACCENT1_LIGHTEST,
        cardSx: {
          cursor: "default",
          ...(isClickable && {
            cursor: "pointer"
          }),
          ...(!displayOnly && {
            transition: "outline 0.1s linear",
            "&:hover": {
              outline: `2px solid ${COLORS.ACCENT1}`
            }
          })
        },
        icon: MoreAltIcon,
        label: "In Progress",
        labelColor: COLORS.ACCENT1_LIGHT,
        tooltipText: `This test has been requested and the ordering process is underway. Once you receive
          your sample collection kit, please return your sample promptly. Your results will be
          available several weeks after the lab receives your sample.`
      };
    }

    if (suggested) {
      return {
        action: () => isSelectable && onSelect?.(productIdentifier),
        actionText: !selected ? "Add" : "Remove",
        cardSx: {
          cursor: "default",
          ...(isClickable && {
            cursor: "pointer",
            transition: "outline 0.1s linear",
            "&:hover": {
              outline: `2px solid ${COLORS.ACCENT3}`
            }
          })
        },
        icon: selected ? CheckmarkCircleIcon : CirclePlusIcon,
        label: "Suggested",
        labelColor: COLORS.ACCENT3_LIGHT,
        tooltipText: `This test is suggested based on the information you shared to support your health goals.`
      };
    }

    return {
      actionLink:
        "https://www.member.genomiclife.com/content/help-center/questions/#ref1401",
      actionText: "Why not suggested",
      cardSx: {
        cursor: "default",
        ...(isClickable && {
          cursor: "not-allowed"
        }),
        outline: `2px solid ${COLORS.PRIMARY_LIGHTEST}`
      },
      icon: LockIcon,
      label: "Not Suggested",
      labelColor: COLORS.PRIMARY_LIGHTEST,
      tooltipText: `Based on the information you shared, this test isn't suggested for you at this time. If you have questions or updates to your health history, please let us know.`
    };
  }, [
    downloadFiles,
    hasFilesReadyToDownload,
    onSelect,
    selected,
    suggested,
    userHasOrder
  ]);
  const {
    action,
    actionLink,
    actionText,
    cardColor,
    cardSx = {},
    icon,
    label,
    labelColor,
    showAboutTest = true,
    tooltipText = ""
  } = config;
  const hasActionLink = actionLink?.length > 0;
  const hasActionText = actionText?.length > 0;
  const hasTooltipText = tooltipText?.length > 0;
  const chipComponent = (
    <Chip
      label={
        <Typography variant="body_strong">
          <Translate text={label} />
        </Typography>
      }
      sx={{
        alignItems: "center",
        backgroundColor: labelColor,
        boxSizing: "border-box",
        display: "flex",
        paddingX: {
          xs: "20px",
          lg: 3
        },
        height: "44px"
      }}
    />
  );

  return (
    <Box
      component="a"
      id={`suggested-screening-card-${kit.glTestType}`}
      href={href}
      onClick={(event) => {
        if (action) {
          event.preventDefault();
          action();
        }
      }}
      sx={{
        outline: "none",
        textDecoration: "none"
      }}
    >
      <Card
        sx={{
          alignItems: "center",
          background: suggested
            ? COLORS.ACCENT3_LIGHTEST
            : cardColor ?? COLORS.WHITE,
          boxShadow: "none",
          borderRadius: "30px 0px 30px 30px",
          display: "flex",
          flexDirection: {
            xs: displayOnly ? "row" : "column-reverse",
            lg: displayOnly ? "row" : "column"
          },
          gap: {
            xs: 3,
            lg: 8
          },
          padding: {
            xs: "32px 24px",
            lg: "30px 30px 30px 20px"
          },
          maxWidth: "384px",
          ...cardSx,
          ...sx
        }}
        onClick={() => isSelectable && onSelect?.(productIdentifier)}
      >
        {!displayOnly && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%"
            }}
          >
            {hasTooltipText ? (
              <GLTooltip placement="bottom" title={tooltipText}>
                {chipComponent}
              </GLTooltip>
            ) : (
              chipComponent
            )}
            <Box
              component="img"
              alt={label}
              src={icon}
              sx={{ height: "44px", width: "44px" }}
            />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: displayOnly ? "row" : "column",
            gap: {
              xs: 2,
              lg: displayOnly ? 2 : "30px"
            }
          }}
        >
          <Box
            component="img"
            alt={testName}
            src={cardImage}
            sx={{
              height: displayOnly
                ? "50px"
                : {
                    xs: "50px",
                    lg: "139px"
                  },
              width: displayOnly
                ? "50px"
                : {
                    xs: "50px",
                    lg: "139px"
                  }
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: displayOnly ? 1 : "15px",
              textAlign: "left"
            }}
          >
            <Typography
              sx={{
                typography: { xs: "body_strong", lg: "body_strong_lg" }
              }}
            >
              {testName}
            </Typography>
            {hasTestDescription && (
              <Typography
                sx={{ height: { xs: "auto", lg: "45px" } }}
                variant="body_xs_dark_gray"
              >
                {testDescription}
              </Typography>
            )}
            {!displayOnly && (
              <Box
                sx={{
                  display: {
                    xs: "none",
                    lg: "flex"
                  },
                  gap: 3
                }}
              >
                {hasActionText &&
                  (hasActionLink ? (
                    <Box
                      component="a"
                      href={actionLink}
                      onClick={(event) => event.stopPropagation()}
                      rel="noopener"
                      target="_blank"
                    >
                      <Typography
                        sx={{ textDecoration: "underline" }}
                        variant="body_strong_xs_dark_gray"
                      >
                        {actionText}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      sx={{ textDecoration: "underline" }}
                      variant="body_strong_xs_dark_gray"
                    >
                      {actionText}
                    </Typography>
                  ))}
                {showAboutTest && aboutTestLink?.length > 0 && (
                  <Box
                    component="a"
                    href={aboutTestLink}
                    onClick={(event) => event.stopPropagation()}
                    rel="noopener"
                    target="_blank"
                  >
                    <Typography
                      sx={{ textDecoration: "underline" }}
                      variant="body_strong_xs_dark_gray"
                    >
                      {`About this test`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
export default SuggestedScreeningCard;
